import { useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import { SectionHeader } from '../SectionHeader'
import type { TestimonialData } from '@/data/solutions/types'
import type {
  CaseStudiesSectionData,
  CaseStudyTestimonial
} from '@/data/inc/homepage-types'

type CaseStudiesProps = {
  data: CaseStudiesSectionData
}

export const CaseStudies = ({ data }: CaseStudiesProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 5000, stopOnInteraction: false })
  ])
  const [selected, setSelected] = useState(0)

  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap())
    emblaApi.on('select', onSelect)
    onSelect()
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])

  const scrollTo = (index: number) => emblaApi?.scrollTo(index)

  if (!data.testimonials) return null

  return (
    <div className="flex flex-col gap-8">
      <SectionHeader
        label={data.label}
        icon={<></>}
        heading={data.heading}
        color="inc-primary-20"
      />

      {/* Carousel */}
      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-10">
            {data.testimonials.map((testimonial, index) => (
              <div key={index} className="flex-[0_0_100%] min-w-0">
                <TestimonialCard testimonial={testimonial} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2">
        {data.testimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={`w-2 h-2 rounded-full transition-all ${
              selected === index ? 'bg-black' : 'border border-black'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

const TestimonialCard = ({
  testimonial
}: {
  testimonial: TestimonialData | CaseStudyTestimonial
}) => {
  return (
    <div className="flex flex-col md:flex-row gap-12 items-center bg-[hsla(0,0%,96%,1)] rounded-3xl p-8 md:p-12 max-w-5xl mx-auto">
      {/* Text content - Left side */}
      <div className="flex-1 flex flex-col gap-6">
        <img src="/assets/inc/home/quotes.svg" alt="" className="w-8" />
        <p className="text-xl md:text-2xl leading-relaxed">
          {testimonial.quote}
        </p>
        <div className="flex flex-col gap-1">
          <p className="font-semibold text-lg">{testimonial.author.name}</p>
          <p className="text-gray-600">{testimonial.author.designation}</p>
          {testimonial.author.companyLogo && (
            <div className="mt-4">
              <img
                src={testimonial.author.companyLogo}
                alt="company logo"
                className="h-8 w-auto object-contain"
              />
            </div>
          )}
        </div>
      </div>

      {/* Image - Right side */}
      {testimonial.author.authorImage && (
        <div className="shrink-0 w-full md:w-[300px] lg:w-[400px]">
          <img
            src={testimonial.author.authorImage}
            alt={testimonial.author.name}
            className="w-full h-auto rounded-2xl object-cover"
          />
        </div>
      )}
    </div>
  )
}
